import type { BriefGap, BriefGapAction } from '@travel-planner/shared/schemas';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { pickCss } from '@/shared/lib/css-utils';
import { BriefGapCard } from './brief-gap-card';
import css from './brief-gaps-panel.module.scss';

const styles = {
  root: pickCss(css, css.root),
  header: pickCss(css, css.header),
  icon: pickCss(css, css.icon),
  label: pickCss(css, css.label),
  chevron: pickCss(css, css.chevron),
  list: pickCss(css, css.list),
};

type BriefGapsPanelProps = {
  gaps: ReadonlyArray<BriefGap>;
  onAction: (gap: BriefGap, action: BriefGapAction) => void;
  defaultCollapsed?: boolean;
};

/**
 * BriefGapsPanel — pen component `XfWXI`. A collapsible amber section: the
 * header summarises the open-gap count and every gap is rendered as a
 * full `BriefGapCard`. The previous compressed secondary list had a
 * click-anywhere-to-ask handler that surprised consultants by appending
 * questions to the draft — every gap now requires an explicit click on
 * the card's "Ask client" button. Rendered on Step 1 (Conversation)
 * between the message history and the AI draft divider.
 */
export const BriefGapsPanel = ({
  gaps,
  onAction,
  defaultCollapsed = false,
}: BriefGapsPanelProps) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  if (gaps.length === 0) return null;

  return (
    <section className={styles.root()}>
      <button
        aria-expanded={!collapsed}
        className={styles.header()}
        onClick={() => setCollapsed((c) => !c)}
        type="button"
      >
        <span aria-hidden="true" className={styles.icon()}>
          <AlertTriangle size={13} />
        </span>
        <span className={styles.label()}>
          {gaps.length} open {gaps.length === 1 ? 'gap' : 'gaps'} — safe to
          start day picks
        </span>
        <span aria-hidden="true" className={styles.chevron()}>
          {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
        </span>
      </button>
      {!collapsed && (
        <div className={styles.list()}>
          {gaps.map((gap) => (
            <BriefGapCard gap={gap} key={gap.id} onAction={onAction} />
          ))}
        </div>
      )}
    </section>
  );
};
